// src/components/HomeLink.js
//
// Small "return to start" link shown only to anonymous (non-authenticated)
// users on tool pages (triage, calculator, autónomo calculator) — pages
// where AppShell hides the header entirely for anon visitors (see
// AppShell.js's showChrome comment), leaving no wordmark/nav to click back
// to mid-flow. Authenticated users already have the real header for this,
// so this component renders nothing for them — pass show={!isAuthenticated}
// (or the equivalent mode check) from the caller.
//
// Two mutually exclusive modes — pass exactly one:
//   - onClick: for a same-route state transition rather than a real
//     navigation. No current caller needs this anymore — TriagePage.js and
//     CalculatorPage.js both used to have a local "intro" screen this
//     pointed back to, and both intros are gone (see
//     portal_start_page_plan.md — '/' is the real front door now, so the
//     intros were pure duplication of it). Left in the component's API
//     rather than deleted, in case a future page has a genuine same-URL
//     "step" concept worth falling back to — but as of this change, every
//     real caller uses `to`.
//   - to: a genuine route change. Now the only mode any current caller
//     uses — always `to="/"` via AnonHeader below, which is what every
//     anonymous-visible page should use instead of calling HomeLink
//     directly (see AnonHeader's own comment).

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Wordmark from './Wordmark'
import SignInModal from './SignInModal'

// Same visual as the intro nav bars' wordmark (.triage-intro-wordmark),
// just reused here as the clickable "home" affordance instead of plain
// text — one definition, via the shared Wordmark component, rather than
// hand-typing the mark a fifth place.
const DEFAULT_LABEL = (
  <div className="triage-intro-wordmark triage-intro-wordmark--flush">
    <Wordmark accentClassName="triage-start-wordmark__accent" />
  </div>
)

export default function HomeLink({ show, to, onClick, label = DEFAULT_LABEL }) {
  if (!show) return null

  // Both modes render an <a> now — a <button> here was picking up browser
  // default button chrome (background/border) that isn't fully overridden
  // by background:none/border:none alone in some browsers, which is what
  // caused the grey-box look. An anchor has no such default styling, so
  // .u-home-link renders identically whether this is an in-page transition
  // or a real navigation. aria-label keeps this announced as "Home" to
  // screen readers even though the visible label is just the wordmark now,
  // with no literal "home" text in it.
  if (onClick) {
    return (
      <a
        href="#"
        className="u-home-link"
        aria-label="Home"
        onClick={(e) => { e.preventDefault(); onClick() }}
      >
        {label}
      </a>
    )
  }

  return (
    <Link to={to} className="u-home-link" aria-label="Home">
      {label}
    </Link>
  )
}

// ── ProductTag ──────────────────────────────────────────────────────────────
//
// Was previously hand-typed in five separate places (TriagePage.js,
// CalculatorPage.js, and AutonomoCalculatorPage.js's form steps, plus
// CalculatorResult.js and TriageResults.js's result-page nav bars) — each
// one retyping its own product name and its own "· with {partner}"
// formatting. Two concrete bugs came from that drift: CalculatorPage.js's
// tag was accidentally gated to screen === 'step1' only (vanished on every
// later step), and AutonomoCalculatorPage.js's was gated to
// screen === 'personal' only (same bug). The result pages, meanwhile, used
// an entirely separate class (.triage-start-advisor-badge, partner-only,
// no product name) — so the same underlying "which product, which
// partner" fact rendered two visibly different ways depending on whether
// you were mid-form or looking at a result.
//
// This is the one definition now. `product` picks the display name from a
// single map (so a name change or a new product only happens in one
// place); `partnerName` is optional and renders the "· with X" suffix when
// present. `style` is only for the result-page nav-bar context, which
// needs the tag's default margin-bottom zeroed out to sit inline next to
// the wordmark — same reasoning as .triage-intro-wordmark--flush being
// used in that exact spot for the same reason.
const PRODUCT_LABELS = {
  triage:     'Spanish Tax Snapshot',
  calculator: 'Spanish Tax Estimator',
  autonomo:   'Autónomo Calculator',
}

// Advisor-facing — shorter, chip-sized labels for the partner dashboard
// (AdvisorDashboard.js's Activity column, CaseBriefPage.js's product
// cards). Kept alongside PRODUCT_LABELS rather than in a separate file so
// there's one place to look when a product's name needs to change, even
// though the consumer- and advisor-facing strings differ in length/tone.
const ADVISOR_PRODUCT_LABELS = {
  triage:     'Snapshot',
  calculator: 'IRPF',
  autonomo:   'Autónomo',
}

export function getAdvisorProductLabel(product) {
  return ADVISOR_PRODUCT_LABELS[product] || PRODUCT_LABELS[product] || product
}

// Advisor-facing calculator session state labels — a session is either a
// frozen published snapshot or an unpublished draft the client is still
// editing. Used by CaseBriefPage.js and lib/briefUtils.js.
const ADVISOR_SESSION_STATE_LABELS = {
  draft:     'Draft',
  published: 'Published',
}

export function getAdvisorSessionStateLabel(state) {
  return ADVISOR_SESSION_STATE_LABELS[state] || state
}

export function ProductTag({ product, partnerName, style }) {
  const label = PRODUCT_LABELS[product]
  if (!label) return null
  return (
    <div className="triage-product-tag" style={style}>
      {label}
      {partnerName && <span className="triage-product-tag-partner">· with {partnerName}</span>}
    </div>
  )
}

// ── AnonHeader ────────────────────────────────────────────────────────────
//
// Persistent top-of-page affordance for anonymous visitors, used
// identically on every page they can reach without signing in: StartPage
// ('/'), CalculatorPage.js, AutonomoCalculatorPage.js, TriagePage.js.
// Renders the wordmark (always links to '/' — the real front door now)
// plus a "Returning? Sign in" link, and owns its own sign-in modal state
// internally so no page needs its own showSignIn useState anymore.
//
// Also renders ProductTag inline, next to the wordmark (same .u-row-md
// grouping the old intro screens used for wordmark + partner badge) —
// callers should NOT render a separate <ProductTag> below this anymore.
// ProductTag already returns null on an unrecognized `product` (e.g.
// StartPage's, which isn't tied to one journey), so this is safe to
// always include.
//
// This replaces three things that each page used to hand-roll separately:
//   1. HomeLink in onClick mode, pointing back to a local "intro" screen
//      (now deleted — see portal_start_page_plan.md) or, on
//      AutonomoCalculatorPage.js, `to="/calculator"` (its sibling page's
//      now-deleted intro).
//   2. A page-local showSignIn boolean + inline <SignInModal>, present
//      only inside the old intro screens — meaning "Returning? Sign in"
//      only existed on the very first screen anonymous visitors saw.
//      AnonHeader is rendered on every screen now, not just the start of
//      a flow.
//   3. A separate <ProductTag> call rendered as a sibling below this,
//      which stacked visually instead of sitting inline (its default
//      margin-bottom is meant for block placement elsewhere, e.g.
//      standalone above a form).
//
// `product` is passed straight to SignInModal (same values it already
// accepted: 'calculator' | 'autonomo' | 'triage') AND to ProductTag.
// `onSignInSuccess` is typically AuthContext's `redirectAfterSignIn`.
export function AnonHeader({ show, product, partnerName, onSignInSuccess }) {
  const [showSignIn, setShowSignIn] = useState(false)
  if (!show) return null

  return (
    <>
      <div className="triage-start-nav">
        <div className="u-row-md">
          <HomeLink show to="/" />
          <ProductTag product={product} partnerName={partnerName} style={{ marginBottom: 0 }} />
        </div>
        <button className="triage-signin-link" onClick={() => setShowSignIn(true)}>
          Returning? Sign in
        </button>
      </div>
      {showSignIn && (
        <SignInModal
          product={product}
          onClose={() => setShowSignIn(false)}
          onSuccess={() => { setShowSignIn(false); onSignInSuccess?.() }}
        />
      )}
    </>
  )
}

// ── PartnerIntroScreen ──────────────────────────────────────────────────────
//
// One-time-per-anonymous-visit leading "step" shown before the real first
// question on CalculatorPage.js/AutonomoCalculatorPage.js/TriagePage.js, for
// partner-referred visitors only (see ticket 014-partnerintro). Renders below
// AnonHeader, replacing that page's normal first-step content for as long as
// the caller's own showPartnerIntro state is true.
//
// No fallback/placeholder name — per ticket 014's D2/D6 decision, each
// caller's own decision logic guarantees this is never rendered until the
// real partner name has resolved, so partnerName is always truthy here.

const PARTNER_INTRO_COPY = (partnerName) =>
  `${partnerName} has teamed up with HolaSam to help make your first conversation more productive. ` +
  `Complete this form to get personalized results, then press "Send\u00A0to\u00A0${partnerName}\u00A0→" at the end, ` +
  `to securely share your information. `

export function PartnerIntroScreen({ partnerName, onContinue }) {
  return (
    <div className="triage-form">
      <p className="triage-hint">{PARTNER_INTRO_COPY(partnerName)}</p>
      <button className="triage-next" onClick={onContinue}>
        Get started →
      </button>
    </div>
  )
}

// ── START_PAGE_CARDS ─────────────────────────────────────────────────────────
//
// Copy for the '/' start page's product cards (StartPage.js). Kept here
// alongside PRODUCT_LABELS/ADVISOR_PRODUCT_LABELS — same "one place per
// product" reasoning as those maps, even though this one serves a
// different UI job (a start-page card, not a mid-form tag or a dashboard
// chip).
//
// IMPORTANT: calculator and autónomo are folded into ONE card here, not
// two. Per calculators_reference.md there is one canonical
// CalculatorSession and one edge function (calculate-irpf) behind both
// /calculator and /autonomo-calculator — is_autonomo is a session-level
// fact (whether self-employment income is present), not a different
// product. Someone who's already run either journey shows exactly one
// "done" calculator card, never two. PRODUCT_LABELS above still keeps
// 'calculator'/'autonomo' as separate keys because that map serves the
// in-flow ProductTag (mid-form, on the page for that specific journey) —
// a different job with a different, correct reason to distinguish them.
//
// features: pulled from the two now-deleted intro screens' checklists
// (CalculatorPage.js's two entry cards, TriagePage.js's own card) rather
// than invented fresh — see the session that removed those intros for
// where each line came from. Calculator's list is a merged/deduped set
// covering both journeys' checklists, since the card itself is one card
// now, not two.
export const START_PAGE_CARDS = {
  triage: {
    title: 'Spain Tax Snapshot',
    description: 'Your Spain-focused residency, filing, and reporting snapshot.',
    features: [
      'Spain tax residency analysis',
      'Spain filing obligations & deadlines',
      'Key reporting requirements (Modelos)',
      'Personalised next steps for Spain',
    ],
    startLabel: 'Start Snapshot →',
    doneLabel: 'View your Snapshot →',
  },
  calculator: {
    title: 'IRPF Calculator',
    description: 'A step-by-step estimate of your Spanish income tax — including autónomo/RETA if you have self-employment income.',
    features: [
      'Salary, autónomo, rentals & investments',
      'Pensions, retirement & foreign income (incl. US)',
      'RETA / Modelo 130 for self-employment',
      'Regional tax differences',
    ],
    // One card-level icon (ti-calculator, set in StartPage.js next to the title,
    // balancing the 🇪🇸 flag on the Snapshot card) replaces the two
    // per-button icons this used to have — one icon per card reads
    // cleaner than one per button once there's a title-level icon at all.
    // General listed first: it's the more common case (no self-employment
    // income), Autónomo second for the minority with a business. Neither
    // is labeled as "the" default — this is a genuine either/or based on
    // the visitor's own situation.
    startOptions: [
      { label: 'Start General Calculator →',  to: '/calculator',         event: 'calculator_started' },
      { label: 'Start Autónomo Calculator →', to: '/autonomo-calculator', event: 'autonomo_calculator_started' },
    ],
    resumeLabel: 'Resume your draft →',
    doneLabel: 'View your estimate →',
  },
}